import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import {
  COLOR_SCALE,
  SHADOW_SCALE,
  Z_INDEX,
  GAP_SCALE,
  SURFACE_PADDING,
  POSITION_SCALE,
  SIZE_SCALE,
  TYPE_LEADING,
  TYPE_SCALE,
  RADIUS_SCALE,
  BORDER_WIDTH,
  ACCESSIBILITY_SCALE,
} from '@/components/design-system/tokens';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      `fixed inset-0 z-${Z_INDEX.modal} ${COLOR_SCALE['sheet-overlay']} data-[state=open]:animate-in data-[state=closed]:animate-out`,
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  `fixed z-${Z_INDEX.modal} ${GAP_SCALE['4']} ${COLOR_SCALE.surface} ${SURFACE_PADDING['6']} ${SHADOW_SCALE.lg} transition ease-in-out data-[state=closed]:duration-150 data-[state=open]:duration-150 data-[state=open]:animate-in data-[state=closed]:animate-out`,
  {
    variants: {
      side: {
        top: `inset-x-0 ${POSITION_SCALE.top['0']} ${BORDER_WIDTH.thin}-b ${RADIUS_SCALE['2xl']}-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top`,
        bottom: `inset-x-0 ${POSITION_SCALE.bottom['0']} ${BORDER_WIDTH.thin}-t ${RADIUS_SCALE['2xl']}-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
        left: `inset-y-0 ${POSITION_SCALE.left['0']} ${SIZE_SCALE.screen} ${SIZE_SCALE['3/4']} ${BORDER_WIDTH.thin}-r ${RADIUS_SCALE['2xl']}-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:${SIZE_SCALE.wsm}`,
        right: `inset-y-0 ${POSITION_SCALE.right['0']} ${SIZE_SCALE.screen} ${SIZE_SCALE['3/4']} ${BORDER_WIDTH.thin}-l ${RADIUS_SCALE['2xl']}-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:${SIZE_SCALE.wsm}`,
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  children: React.ReactNode;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, ...props }, ref) => {
  const { t } = useTranslation();
  return (
    <SheetPortal container={typeof document !== 'undefined' ? document.body : undefined}>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        <SheetPrimitive.Close
          className={`absolute ${POSITION_SCALE.right['4']} ${POSITION_SCALE.top['4']} ${RADIUS_SCALE.sm} opacity-70 ring-offset-background transition-opacity duration-150 ease-in-out hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:${COLOR_SCALE.secondary}`}
        >
          <X className={`${SIZE_SCALE['4']}`} aria-hidden="true" />
          <span className={ACCESSIBILITY_SCALE.srOnly}>{t('common', 'close') || 'Close'}</span>
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(`flex flex-col ${GAP_SCALE.sm} text-center sm:text-left`, className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(`flex flex-col-reverse sm:flex-row sm:justify-end sm:${GAP_SCALE.sm}`, className)}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      `${TYPE_SCALE.lg} font-semibold ${COLOR_SCALE.foreground} ${TYPE_LEADING.snug} break-words`,
      className
    )}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn(
      `${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']} ${TYPE_LEADING.relaxed} break-words`,
      className
    )}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
